/**
 * DeliverySummaryTab Component
 * 배달 요약 탭 (기존 홈 페이지)
 */
import React, { useRef, useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import { StatsCard } from './shared/StatsCard';
import { FeeCard } from './shared/FeeCard';
import { SlotsCard } from './shared/SlotsCard';
import { useHomePageForRider } from '../../hooks/useHomePageForRider';
import { useDeferredLoading } from '../../../../core/hooks/useDeferredLoading';
import { getUserInfo } from '../../../../core/utils';
import styles from '../styles/DeliverySummaryTab.module.css';

export const DeliverySummaryTab: React.FC = () => {
    const navigate = useNavigate();
    const dateRef = useRef<HTMLInputElement>(null);

    const isSupplier = useMemo(() => {
        const userInfo = getUserInfo();
        return !!userInfo?.suppId;
    }, []);

    const {
        deliveryStats,
        selectedDate,
        dateOptions,
        isLoading: rawIsLoading,
        handleDateChange,
    } = useHomePageForRider();

    const isLoading = useDeferredLoading(rawIsLoading, 200);

    const formatDateToYyyymmdd = (dateString: string): string => {
        if (!dateString) return '';
        return dateString.replace(/-/g, '');
    };

    const handleSlotClick = (slotType: string) => {
        if (isSupplier || !selectedDate) {
            return;
        }

        const params = new URLSearchParams();
        params.set('fromDate', formatDateToYyyymmdd(selectedDate));
        params.set('toDate', formatDateToYyyymmdd(selectedDate));
        params.set('slotType', slotType);

        navigate(`/delivery-history/detail?${params.toString()}`);
    };

    const handleCalendarClick = () => {
        if (dateRef.current) {
            try {
                dateRef.current.focus();
                // showPicker가 지원되는 경우에만 호출
                if (typeof dateRef.current.showPicker === 'function') {
                    dateRef.current.showPicker();
                } else {
                    // showPicker가 지원되지 않으면 클릭 이벤트 발생
                    dateRef.current.click();
                }
            } catch (error) {
                console.log('Calendar picker not supported:', error);
                // 에러 발생 시 클릭으로 대체
                dateRef.current.click();
            }
        }
    };

    return (
        <>
            {/* 날짜 선택 헤더 */}
            <div className={styles['summary-header']}>
                <div className={styles['date-selector']}>
                    <div className={styles['date-select-box']}>
                        <select
                            className={styles['date-select']}
                            value={selectedDate}
                            onChange={(e) => handleDateChange(e.target.value)}
                        >
                            {dateOptions && dateOptions.map((date) => (
                                <option key={date.value} value={date.value}>
                                    {date.label}
                                </option>
                            ))}
                        </select>
                    </div>
                    <div className={styles['calendar-btn-wrapper']}>
                        <button className={styles['calendar-btn']} type="button">
                            <i className="fas fa-calendar-alt"></i>
                        </button>
                        <input
                            ref={dateRef}
                            type="date"
                            className={styles['calendar-input']}
                            value={selectedDate}
                            onChange={(e) => handleDateChange(e.target.value)}
                            max={new Date().toISOString().split('T')[0]}
                        />
                    </div>
                </div>
            </div>

            {/* 메인 컨텐츠 */}
            <div className={styles['summary-container']}>
                {isLoading ? (
                    <div className={styles['loading-section']}>
                        <div className={styles['loading-spinner']}></div>
                        <p className={styles['loading-text']}>데이터를 불러오는 중...</p>
                    </div>
                ) : (
                    <>
                        <StatsCard
                            completedCount={deliveryStats.completedCount}
                            rejectedCount={deliveryStats.rejectedCount}
                            acceptanceRate={deliveryStats.acceptanceRate}
                        />
                        
                        <FeeCard deliveryFee={deliveryStats.deliveryFee} />
                        
                        <SlotsCard
                            morningLunchPeakCount={deliveryStats.morningLunchPeakCount}
                            afternoonNonPeakCount={deliveryStats.afternoonNonPeakCount}
                            eveningPeakCount={deliveryStats.nightPeakCount}
                            nightPeakCount={deliveryStats.eveningPeakCount}
                            onSlotClick={handleSlotClick}
                            clickable={!isSupplier}
                        />
                    </>
                )}
            </div>
        </>
    );
};
